import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { IExportSalePlan, IParams } from 'apis/GraphQL/Variables/salePlan';
import { PlantApi } from 'apis/PlantApis';
import SalePlanApi from 'apis/SalePlanApis';
import { Calendar } from 'assets/svg';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonImport from 'components/Button/ButtonImport';
import { UploadModal } from 'components/UploadModal_V2';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import usePagination from 'hooks/usePagination';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import CreateSalePlanningAuto from 'pages/Plant/SalePlan/CreateSalePlanningAuto';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import path from 'routes/Path';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IMPORT_SALES_PLANNING } from 'utils/constants/importId';
import FilterSalePlan from './FilterSalePlan';
import TableSalePlan from './TableSalePlan';

const { PlusOutlined } = icons;

interface IFilterSalePlanning {
  teamSalesId?: string;
  plantId?: string;
  routeId?: string;
  statusId?: string;
  fromDate?: string;
  toDate?: string;
}

// Tạo tay / Tạo tự động
// Khi khai báo tuyến n ngày > hiển thị n dòng để filter / search như cũ
export default function SalePlan() {
  const { setTitle, setBreadcrumb } = useTitle();
  const navigate = useNavigate();
  const { user } = useUser();
  const defaultFilter = useMemo(() => {
    const userAny = user as any;

    if (user?.isCN)
      return {
        plantId: userAny?.plantCode,
      };

    if (user?.isSale) {
      const teamSale = userAny?.teamSales?.[0];

      return {
        plantId: userAny?.plantCode,
        teamSalesId: teamSale?.id,
        teamSalesSubId: teamSale?.subId,
        teamSalesName: teamSale?.name,
      };
    }

    return {};
  }, [user]);

  const { page, page_size } = usePagination();

  const [isOpenFilter, setIsOpenFilter] = useState(false),
    [isOpenImportModal, setIsOpenImportModal] = useState(false),
    [filterData, setFilterData] = useState<IFilterSalePlanning>(() => ({
      fromDate: dayjs().format('YYYY-MM-DD 00:00:00'),
      toDate: dayjs().endOf('month').format('YYYY-MM-DD 00:00:00'),
    }));
  const [editIndex, setEditIndex] = useState(-1);
  const [plants, setPlants] = useState({});
  const [isAdd, setIsAdd] = useState(false);
  const [data, setData] = useState<{
    items: any;
    pageInfo: any;
  }>({
    items: [],
    pageInfo: {},
  });

  const tableRef = useRef<any>(null);
  const [isOpenModalCreateDashboard, setIsOpenModalCreateDashboard] =
    useState(false);

  const columns: ColumnsTypeCustom = useMemo(
    () => [
      {
        isShowRender: true,
        type: 'number',
        title: 'STT',
        dataIndex: 'index',
        render: (value) => value,
      },
      {
        title: 'Ngày bắt đầu',
        dataIndex: 'implementationDate', //chưa có
        type: 'date',
        formatDate: 'DD/MM/YYYY',
      },
      {
        title: 'Ngày kết thúc',
        dataIndex: 'implementationEndDate', //chưa có
        type: 'date',
        formatDate: 'DD/MM/YYYY',
      },
      {
        title: 'Chi nhánh',
        dataIndex: 'plantId',
      },
      {
        title: 'Mã đội',
        dataIndex: 'teamId',
      },
      {
        title: 'Mã tuyến',
        dataIndex: 'routeId',
      },
      {
        title: 'Tên tuyến',
        dataIndex: 'routeName',
      },
      {
        title: 'Tên đội',
        dataIndex: 'teamName',
      },
      {
        type: 'number',
        currency: 'vi-VN',
        title: 'Doanh thu chuyến dự kiến',
        dataIndex: 'expectedRevenue',
      },
      {
        title: 'Tiền tệ',
        dataIndex: 'unitMoney',
      },
      {
        title: 'Năng lực',
        dataIndex: 'teamCapacity',
      },
      {
        title: 'Trạng thái',
        dataIndex: 'statusName',
      },
      {
        title: 'Mã khách hàng',
        dataIndex: ['tcustomer', 'sapCustomerId'],
      },
      // {
      //   title: 'Mã DMS',
      //   dataIndex: ['tcustomer', 'id'],
      // },
      {
        title: 'Tên cửa hàng',
        dataIndex: ['tcustomer', 'storeName'],
      },
      {
        title: 'VIP',
        dataIndex: ['tcustomer', 'trank', 'name'],
      },
      {
        title: 'MST',
        dataIndex: ['tcustomer', 'taxId'],
      },
      {
        title: 'SDT',
        dataIndex: ['tcustomer', 'phone'],
      },
      {
        title: 'Địa chỉ chi tiết',
        dataIndex: ['tcustomer', 'address'],
      },
      {
        title: 'Phường/xã',
        dataIndex: ['tcustomer', 'tward', 'name'],
      },
      {
        title: 'Quận/huyện',
        dataIndex: ['tcustomer', 'tdistrict', 'name'],
      },
      {
        title: 'Tỉnh/thành phố',
        dataIndex: ['tcustomer', 'tcity', 'name'],
      },
      {
        title: 'Người tạo',
        width: 160,
        dataIndex: 'createName',
      },
      {
        title: 'Ngày tạo',
        dataIndex: 'createDate',
        type: 'date',
      },
      {
        title: 'Người cập nhật',
        dataIndex: 'modifyName',
      },
      {
        title: 'Ngày cập nhật',
        dataIndex: 'modifyDate',
        type: 'date',
      },
    ],
    [],
  );

  const { mutate: getAllPlant } = useMutation({
    mutationFn: () => PlantApi.getAllPlantComboBox(),
    onSuccess: (data: any) => {
      const items = data.data?.getPlant?.items || [];

      if (!items?.length) return setPlants({ items: [], pageInfo: {} });
      setPlants(() => ({
        items: items.map((item: any) => ({
          label: item.id,
          value: item.id,
        })),
        pageInfo: data.data.getPlant.pageInfo,
      }));
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { isPending, mutate: getAllSalePlan } = useMutation({
    mutationFn: (params: IParams) => SalePlanApi.getSalePlanListNew(params),
    onSuccess: (data: any) => {
      const items = (data.data?.getSalesPlanningList?.items || []).map(
        (item: any) => {
          return {
            ...item,
            routeName: item.routeName,
            areaName: item.tplant?.areaName, //
            teamTypeId: item?.teamSales?.teamTypeId,
            teamSalesName: item?.teamSales?.name,
            tplantName: item?.tplant?.name, //
            numExpectedDays: item?.numExpectedDays, //
            statusName: item?.statusName,
            modifyName: item?.modifyName,
            unitMoney: 'triệu VND',
          };
        },
      );

      setData({
        items,
        pageInfo: data.data?.getSalesPlanningList?.pageInfo,
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const {
    isPending: isPendingExportSalePlanning,
    mutateAsync: exportSalePlanning,
  } = useMutation({
    mutationFn: (params: IExportSalePlan) =>
      SalePlanApi.exportSalePlanList(params),
    onError: () => {
      toastError('Có lỗi xảy ra khi xuất dữ liệu');
    },
  });

  const mutationCreateDashboard = useMutation({
    mutationFn: SalePlanApi.createDashboard,
  });

  const reFetchSalePlan = useCallback(() => {
    if (user?.isSale && !defaultFilter.teamSalesSubId) return;

    getAllSalePlan({
      page,
      page_size,
      ...filterData,
      ...defaultFilter,
      teamSalesId: filterData?.teamSalesId || defaultFilter?.teamSalesSubId,
    });
  }, [
    user?.isSale,
    defaultFilter,
    getAllSalePlan,
    page,
    page_size,
    filterData,
  ]);

  useEffect(() => {
    setTitle('Lập kế hoạch bán hàng');
    setBreadcrumb([{ title: 'Kế hoạch' }, { title: 'Lập kế hoạch bán hàng' }]);

    getAllPlant();
  }, [getAllPlant, setBreadcrumb, setTitle]);

  useEffect(() => {
    reFetchSalePlan();
  }, [reFetchSalePlan]);

  const handleExportExcel = useCallback(async () => {
    if (user?.isSale && !defaultFilter.teamSalesSubId) return;

    try {
      const res = await exportSalePlanning({
        ...filterData,
        ...defaultFilter,
      });

      const salePlanData = res.data?.getSalesPlanningExport?.items ?? [];
      const processData = salePlanData
        .map((item: any) => ({
          ...item,
          unitMoney: 'triệu VND',
        }))
        .reduce((prev: any[], acc: any, index: number) => {
          const customers = acc?.tcustomer ?? [];

          if (!customers.length)
            return [
              ...prev,
              {
                ...acc,
                index: index + 1,
              },
            ];

          return [
            ...prev,
            ...customers.map((item: any, i: number) => ({
              ...acc,
              tcustomer: {
                ...item,
                sapCustomerId: item.sapCustomerId || item.id,
              },
              index: i ? undefined : index + 1,
            })),
          ];
        }, []);

      exportExcel_v2(
        columns.map((item) => ({
          ...item,
          isShowRender: true,
        })),
        processData,
        'Kế hoạch - Lập kế hoạch bán hàng',
        false,
      );
    } catch (error) {
      //
    }
  }, [columns, defaultFilter, exportSalePlanning, filterData, user?.isSale]);

  const handleOnCancelFilter = () => {
    navigate(`?page=1&page_size=${page_size}`);
    setFilterData({});
  };

  const handleOnOkFilter = (values: any) => {
    navigate(`?page=1&page_size=${page_size}`);
    setFilterData(values);
  };

  const handleImportData = useCallback(() => {
    setIsOpenImportModal(true);
  }, []);

  const checkFormValidate = async (form: FormInstance<any>) => {
    try {
      await form.validateFields(); // Kiểm tra tất cả các trường
      return true; // Trả về true nếu không có lỗi
    } catch (error) {
      return false; // Trả về false nếu có lỗi
    }
  };

  async function handleOK(form: FormInstance<any>) {
    if (!(await checkFormValidate(form))) {
      form.validateFields();
      return;
    }

    const teamSalesIds = form.getFieldValue('teamSalesId') as string[];

    const res = await Promise.allSettled(
      teamSalesIds.map((teamSalesId) =>
        mutationCreateDashboard.mutateAsync({
          team_id: teamSalesId,
          month: parseInt(form.getFieldValue('month')),
          year: parseInt(form.getFieldValue('year').format('YYYY')),
          plantId: form.getFieldValue('plantId'),
        }),
      ),
    );

    const isSuccess = res.some(
      (item: any) => item?.value?.data?.generateSalesPlanning?.success,
    );

    if (isSuccess) {
      reFetchSalePlan();
      // navigate(`?page=1&page_size=${page_size}`);
      setIsOpenModalCreateDashboard(false);
      toastSuccess('Tạo kế hoạch thành công');
    } else {
      const message = (res?.[0] as any)?.value?.data?.generateSalesPlanning
        ?.message;

      toastError(
        message ??
          'Năng lực đội không đủ hoặc không có dữ liệu khách hàng phù hợp để tạo',
      );
      setIsOpenModalCreateDashboard(false);
    }

    form.resetFields();
  }

  function handleCancel(form: FormInstance<any>) {
    setIsOpenModalCreateDashboard(false);
    form.resetFields();
  }

  const handleAndRow = () => {
    tableRef.current?.addRow?.();
  };

  useEffect(() => {
    setIsOpenFilter(false);
  }, [isAdd, editIndex]);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter
            disabled={editIndex !== -1 || isAdd}
            isOpen={isOpenFilter}
            setIsOpen={setIsOpenFilter}
          />
        </Col>
        <Col>
          <ButtonExport disabled={isAdd} onClick={handleExportExcel} />
        </Col>
        <Col>
          <ButtonImport
            disabled={editIndex !== -1 || isAdd}
            onClick={handleImportData}
          />
        </Col>
        <Col>
          <Button
            title="Dashboard"
            handleOnclick={() => {
              navigate(path.TEAM_DASHBOARD);
            }}
          />
        </Col>
        <Col>
          <Button
            disabled={editIndex !== -1 || isAdd}
            fill
            title="Tạo kế hoạch bán hàng tự động theo tháng"
            iconBefore={<Calendar />}
            handleOnclick={() => setIsOpenModalCreateDashboard(true)}
          />
        </Col>
        {!user?.isHO && (
          <Col>
            <Button
              fill
              iconBefore={<PlusOutlined />}
              title="Tạo kế hoạch"
              handleOnclick={handleAndRow}
              disabled={editIndex !== -1 || isAdd || isPending}
            />
          </Col>
        )}
      </Row>
      {isOpenFilter && (
        <FilterSalePlan
          isOpenFilter={isOpenFilter}
          isAdd={isAdd}
          optionPlants={plants}
          defaultFilter={defaultFilter}
          filterData={filterData}
          onCancel={handleOnCancelFilter}
          onFilter={handleOnOkFilter}
        />
      )}
      <TableSalePlan
        filterData={filterData}
        defaultFilter={defaultFilter}
        isAdd={isAdd}
        setIsAdd={setIsAdd}
        data={data}
        setData={setData}
        editIndex={editIndex}
        setEditIndex={setEditIndex}
        refetch={reFetchSalePlan}
        ref={tableRef}
        loading={isPending || isPendingExportSalePlanning}
        optionPlants={plants}
        setFilterData={setFilterData}
      />
      <UploadModal
        importId={IMPORT_SALES_PLANNING}
        templateFileName="Kế hoạch - Lập kế hoạch bán hàng - Template"
        open={isOpenImportModal}
        title={'Thêm mới kế hoạch bán hàng'}
        setOpen={setIsOpenImportModal}
        refetch={reFetchSalePlan}
      />

      <CreateSalePlanningAuto
        defaultFilter={defaultFilter}
        handleCancel={handleCancel}
        handleOK={handleOK}
        open={isOpenModalCreateDashboard}
      />
    </>
  );
}
